import { useState, useEffect } from "react";
import { t } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import {
  ArrowBendUpLeft, ChatCircle, ArrowBendUpRight, Star, EnvelopeSimple,
  FolderSimple, FolderOpen, Code, Trash, X, PaperPlaneTilt, PencilSimple, CaretDown, CaretUp, Warning
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { toast } from "sonner";
import api, { errDetail } from "@/lib/api";

export const ThreadView = ({ thread, userEmail, onClose, onChanged, onAskAgent, categories = [], mailbox = "" }) => {
  const mbParams = mailbox ? { params: { mailbox } } : {};
  const navigate = useNavigate();
  const [editingDraft, setEditingDraft] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [moveOpen, setMoveOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [starred, setStarred] = useState(false);
  const [srcData, setSrcData] = useState(null);
  const [htmlCache, setHtmlCache] = useState({});
  const [plainView, setPlainView] = useState({});
  const [catOpen, setCatOpen] = useState(false);
  const [presence, setPresence] = useState(null);

  useEffect(() => {
    if (!mailbox || !thread?.messages?.length) { setPresence(null); return; }
    const last = thread.messages[thread.messages.length - 1];
    const tid = last.thread_id || last.id;
    let stop = false;
    const beat = async () => {
      try {
        const { data } = await api.post(`/shared-inboxes/${mailbox}/threads/${tid}/presence`, { editing: !!editingDraft });
        if (!stop) setPresence(data);
      } catch { /* noop */ }
    };
    beat();
    const iv = setInterval(beat, 5000);
    return () => {
      stop = true; clearInterval(iv);
      api.post(`/shared-inboxes/${mailbox}/threads/${tid}/presence`, { leaving: true }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, mailbox, editingDraft]);

  useEffect(() => {
    setStarred(!!thread?.messages?.some((m) => m.is_starred));
    setSrcData(null); setPlainView({}); setCatOpen(false);
    (thread?.messages || []).filter((m) => m.was_html).forEach(async (m) => {
      try {
        const { data } = await api.get(`/emails/${m.id}/source`);
        const DOMPurify = (await import("dompurify")).default;
        const clean = data.body_html ? DOMPurify.sanitize(data.body_html, { FORBID_TAGS: ["script", "style", "form", "input"], ADD_ATTR: ["target"] }) : "";
        setHtmlCache((h) => ({ ...h, [m.id]: clean }));
      } catch { setHtmlCache((h) => ({ ...h, [m.id]: "" })); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread]);

  if (!thread) {
    return (
      <div className="flex-1 min-w-0 h-full border-r border-gray-200 flex items-center justify-center bg-white">
        <div className="text-center text-gray-400">
          <EnvelopeSimple size={40} className="mx-auto mb-2 opacity-50" />
          <p data-testid="no-thread-selected" className="text-sm">{t("thread.select_convo", "Select a conversation to read it")}</p>
        </div>
      </div>
    );
  }

  const msgs = thread.messages;
  const latest = msgs[msgs.length - 1];
  const subject = msgs.find((m) => !m.subject.startsWith("Re:"))?.subject || latest.subject;
  const incoming = [...msgs].reverse().find((m) => m.from_email !== userEmail && !m.is_draft);

  const act = async (action) => {
    try {
      await api.post(`/emails/${latest.id}/action`, { action }, mbParams);
      toast.success(action === "trash" ? "Moved to trash" : "Done");
      onChanged();
      if (["trash", "archive", "spam", "delete_forever"].includes(action)) onClose();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const toggleStar = async () => {
    const next = !starred;
    setStarred(next);
    try {
      await api.post(`/emails/${latest.id}/action`, { action: next ? "star" : "unstar" }, mbParams);
      toast.success(next ? t("thread.starred", "Starred ⭐") : t("thread.unstarred", "Star removed"));
      onChanged();
    } catch (e) { setStarred(!next); toast.error(errDetail(e)); }
  };

  const forward = () => {
    const orig = incoming || latest;
    const quoted = `\n\n---------- Forwarded message ----------\nFrom: ${orig.from_name} <${orig.from_email}>\nDate: ${dayjs(orig.created_at).format("MMM D, YYYY h:mm A")}\nSubject: ${orig.subject}\nTo: ${orig.to_email}\n\n${orig.body}`;
    navigate("/compose", { state: { forward: { subject: `Fwd: ${orig.subject}`, body: quoted } } });
  };

  const openSource = async () => {
    try { const { data } = await api.get(`/emails/${(incoming || latest).id}/source`); setSrcData(data); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const setCategory = async (catId) => {
    setCatOpen(false);
    try {
      await api.post(`/emails/${(incoming || latest).id}/category`, { category_id: catId });
      toast.success(catId ? t("cats.set", "Category updated") : t("cats.cleared", "Category removed"));
      onChanged(true);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const openMove = async () => {
    if (!moveOpen) {
      try { const { data } = await api.get("/folders"); setFolders(data); } catch { /* noop */ }
    }
    setMoveOpen(!moveOpen);
  };
  const moveTo = async (fid) => {
    setMoveOpen(false);
    try {
      if (fid === "inbox") await api.post(`/emails/${latest.id}/action`, { action: "inbox" }, mbParams);
      else await api.post(`/emails/${latest.id}/action`, { action: "move", folder_id: fid }, mbParams);
      toast.success("Moved");
      onChanged();
      onClose();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const sendDraft = async (d) => {
    try {
      await api.post(`/drafts/${d.id}/send`);
      toast.success("Reply sent");
      if (mailbox) api.post(`/shared-inboxes/${mailbox}/threads/${d.thread_id || d.id}/presence`, { sent: true }).catch(() => {});
      onChanged(true);
    }
    catch (e) { toast.error(errDetail(e)); }
  };
  const discardDraft = async (d) => {
    try { await api.delete(`/drafts/${d.id}`); toast.success("Draft discarded"); onChanged(true); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const saveDraft = async (d) => {
    try { await api.put(`/drafts/${d.id}`, { body: editBody }); setEditingDraft(null); toast.success("Draft updated"); onChanged(true); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const IconBtn = ({ icon: Icon, onClick, testId, title, danger }) => (
    <button data-testid={testId} title={title} onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${danger ? "text-gray-400 hover:text-red-600 hover:bg-red-50" : "text-gray-400 hover:text-gray-800 hover:bg-gray-100"}`}>
      <Icon size={17} />
    </button>
  );

  return (
    <div className="flex-1 min-w-0 h-full border-r border-gray-200 flex flex-col bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
        <div className="flex items-center gap-0.5">
          <IconBtn icon={ArrowBendUpLeft} testId="thread-reply-btn" title={t("thread.ask_draft", "Ask agent to draft a reply")}
            onClick={() => onAskAgent(`Draft a reply to this email from ${incoming?.from_name || "the sender"}`)} />
          <IconBtn icon={ChatCircle} testId="thread-chat-btn" title={t("thread.discuss", "Discuss with agent")}
            onClick={() => onAskAgent("Summarize this thread and suggest next steps")} />
          <IconBtn icon={ArrowBendUpRight} testId="thread-forward-btn" title={t("thread.forward", "Forward")} onClick={forward} />
          <button data-testid="thread-star-btn" title={t("thread.star", "Star")} onClick={toggleStar}
            className={`p-1.5 rounded-md transition-colors ${starred ? "text-amber-400 hover:text-amber-500 hover:bg-amber-50" : "text-gray-400 hover:text-gray-800 hover:bg-gray-100"}`}>
            <Star size={17} weight={starred ? "fill" : "regular"} />
          </button>
          <IconBtn icon={EnvelopeSimple} testId="thread-unread-btn" title={t("thread.mark_unread", "Mark unread")} onClick={() => act("unread")} />
          <IconBtn icon={FolderSimple} testId="thread-archive-btn" title={t("thread.archive", "Archive")} onClick={() => act("archive")} />
          <div className="relative">
            <IconBtn icon={FolderOpen} testId="thread-move-btn" title={t("thread.move", "Move to folder")} onClick={openMove} />
            {moveOpen && (
              <div data-testid="thread-move-menu" className="absolute z-30 left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                <div className="px-3 py-1 text-[10px] font-semibold tracking-wider text-gray-400">{t("thread.move_to", "MOVE TO")}</div>
                <button data-testid="move-to-inbox" onClick={() => moveTo("inbox")}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Inbox</button>
                {folders.map((f) => (
                  <button key={f.id} data-testid={`move-to-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => moveTo(f.id)}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{f.name}</button>
                ))}
                {folders.length === 0 && <div className="px-3 py-1.5 text-xs text-gray-400">{t("thread.no_custom_folders", "No custom folders yet — create one in the sidebar")}</div>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <IconBtn icon={Code} testId="thread-source-btn" title={t("thread.view_source", "View source")} onClick={openSource} />
          <IconBtn icon={Trash} testId="thread-trash-btn" title={t("thread.delete", "Delete")} danger onClick={() => act("trash")} />
          <IconBtn icon={X} testId="thread-close-btn" title={t("common.close", "Close")} onClick={onClose} />
        </div>
      </div>

      {mailbox && presence?.replying?.length > 0 && (
        <div data-testid="si-collision-banner" className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 shrink-0">
          <Warning size={16} weight="fill" className="shrink-0 text-amber-500" />
          <span>{t("si.collision", "{name} is already replying to this email", { name: presence.replying.map((p) => p.name).join(", ") })}</span>
        </div>
      )}
      {mailbox && !presence?.replying?.length && presence?.recently_sent?.length > 0 && (
        <div data-testid="si-recent-reply-note" className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-800 shrink-0">
          ↩ {t("si.recent_reply", "{name} replied {secs}s ago — check the thread before replying", { name: presence.recently_sent[0].name, secs: presence.recently_sent[0].ago_seconds })}
        </div>
      )}
      <div className="flex-1 overflow-y-auto scroll-thin px-6 py-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 data-testid="thread-subject" className="font-heading text-xl font-bold text-gray-900">{subject}</h1>
          {categories.length > 0 && (() => {
            const curId = (incoming || latest)?.category_id;
            const cur = categories.find((c) => c.id === curId);
            return (
              <div className="relative">
                <button data-testid="thread-category-chip" onClick={() => setCatOpen(!catOpen)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors"
                  style={cur ? { backgroundColor: `${cur.color}1a`, color: cur.color, borderColor: `${cur.color}55` } : { color: "#9ca3af", borderColor: "#e5e7eb" }}>
                  {cur ? (<><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cur.color }} />{cur.name}</>) : `+ ${t("cats.set_label", "Category")}`}
                </button>
                {catOpen && (
                  <div data-testid="thread-category-menu" className="absolute z-30 left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                    {categories.map((c) => (
                      <button key={c.id} data-testid={`set-category-${c.id}`} onClick={() => setCategory(c.id)}
                        className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />{c.name}
                      </button>
                    ))}
                    <button data-testid="set-category-none" onClick={() => setCategory(null)}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-50 border-t border-gray-100">{t("cats.no_cat", "No category")}</button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <p className="text-xs text-gray-400 mb-4">{msgs.filter((m) => !m.is_draft).length} messages in this thread</p>

        {msgs.map((m) => {
          const isDraft = m.is_draft;
          const isCollapsed = collapsed[m.id];
          return (
            <div key={m.id} data-testid={`message-${m.id}`}
              className={`mb-3 rounded-md border fade-up ${isDraft ? "border-l-4 border-l-amber-400 border-gray-200 bg-amber-50/40" : "border-gray-200"}`}>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setCollapsed({ ...collapsed, [m.id]: !isCollapsed })}>
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                  {(m.from_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{isDraft ? "Draft reply" : m.from_name}</span>
                    {!isDraft && m.sent_by_name && (
                      <span data-testid={`sent-by-label-${m.id}`} className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded px-1.5 py-px">
                        ↩ {t("thread.replied_by", "Replied by {name}", { name: m.sent_by_name })}
                      </span>
                    )}
                    {isDraft && <span className="text-[10px] font-semibold bg-gray-200 text-gray-600 rounded px-1.5 py-px">{t("thread.draft", "Draft")}</span>}
                    {isDraft && m.send_at && (
                      <span data-testid={`draft-scheduled-badge-${m.id}`} className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded px-1.5 py-px inline-flex items-center gap-1">
                        ⏰ {t("thread.scheduled", "Scheduled")} · {new Date(m.send_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        <button data-testid={`draft-cancel-schedule-btn-${m.id}`}
                          onClick={async (e) => { e.stopPropagation(); try { await api.post(`/drafts/${m.id}/cancel-schedule`); toast.success(t("thread.schedule_cancelled", "Scheduled send cancelled")); onChanged(true); } catch (err) { /* noop */ } }}
                          className="underline hover:text-amber-900">{t("thread.cancel", "Cancel")}</button>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">To: {m.to_email}</div>
                </div>
                <span className="text-xs text-gray-400 shrink-0">{dayjs(m.created_at).format("MMM D, h:mm A")}</span>
                {isCollapsed ? <CaretDown size={14} className="text-gray-400" /> : <CaretUp size={14} className="text-gray-400" />}
              </button>
              {!isCollapsed && (
                <div className="px-4 pb-4">
                  {editingDraft === m.id ? (
                    <div>
                      {mailbox && presence?.replying?.length > 0 && (
                        <p data-testid="si-collision-compose-warning" className="text-xs font-semibold text-red-600 mb-1.5">
                          ⚠ {t("si.collision_compose", "{name} is also replying — you may send a duplicate reply", { name: presence.replying.map((p) => p.name).join(", ") })}
                        </p>
                      )}
                      <textarea data-testid="draft-edit-textarea" value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={8}
                        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <div className="flex gap-2 mt-2">
                        <button data-testid="draft-save-btn" onClick={() => saveDraft(m)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors">{t("thread.save", "Save")}</button>
                        <button data-testid="draft-cancel-edit-btn" onClick={() => setEditingDraft(null)} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5">{t("thread.cancel", "Cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    m.was_html && htmlCache[m.id] && !plainView[m.id] ? (
                      <div>
                        <div data-testid={`html-body-${m.id}`} className="email-html text-sm text-gray-800 overflow-x-auto"
                          dangerouslySetInnerHTML={{ __html: htmlCache[m.id] }} />
                        <button data-testid={`plain-toggle-${m.id}`} onClick={() => setPlainView({ ...plainView, [m.id]: true })}
                          className="mt-2 text-[11px] text-gray-400 hover:text-gray-600 underline">{t("thread.view_plain", "View plain text")}</button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{m.body}</p>
                        {m.was_html && htmlCache[m.id] && plainView[m.id] && (
                          <button data-testid={`html-toggle-${m.id}`} onClick={() => setPlainView({ ...plainView, [m.id]: false })}
                            className="mt-2 text-[11px] text-blue-500 hover:text-blue-700 underline">{t("thread.view_html", "View HTML")}</button>
                        )}
                      </div>
                    )
                  )}
                  {(m.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {m.attachments.map((a) => (
                        a.source === "gmail" ? (
                          <span key={a.id} data-testid={`attachment-gmail-${a.id}`}
                            className="flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-700">
                            📎 {a.name} <span className="text-gray-400">({Math.max(1, Math.round((a.size || 0) / 1024))} KB)</span>
                            {a.parse_status === "parsed" && <span data-testid={`attachment-parsed-${a.id}`} title={t("thread.readable", "Readable by the agent")} className="text-green-600 font-bold">✓</span>}
                            {a.parse_status === "failed" && <span data-testid={`attachment-failed-${a.id}`} title={t("thread.unreadable", "The agent couldn't read this file")} className="text-amber-600 font-bold">⚠</span>}
                            {a.parse_status === "pending" && <span data-testid={`attachment-pending-${a.id}`} title={t("thread.processing", "Still processing")} className="text-gray-400">⏳</span>}
                          </span>
                        ) : (
                        <button key={a.id} data-testid={`attachment-download-${a.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/attachments/${a.id}/ticket`);
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}/download?ticket=${data.ticket}`, "_blank");
                            } catch (e) { toast.error(errDetail(e)); }
                          }}
                          className="flex items-center gap-1 bg-gray-100 border border-gray-200 hover:border-blue-300 rounded-md px-2 py-1 text-xs text-gray-700 transition-colors">
                          📎 {a.name} <span className="text-gray-400">({Math.max(1, Math.round((a.size || 0) / 1024))} KB)</span>
                        </button>
                        )
                      ))}
                    </div>
                  )}
                  {isDraft && editingDraft !== m.id && (
                    <div className="flex items-center gap-2 mt-4">
                      <button data-testid={`draft-send-btn-${m.id}`} onClick={() => sendDraft(m)}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors">
                        <PaperPlaneTilt size={14} /> Send
                      </button>
                      <button data-testid={`draft-edit-btn-${m.id}`} onClick={() => { setEditingDraft(m.id); setEditBody(m.body); }}
                        className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md px-4 py-1.5 transition-colors">
                        <PencilSimple size={14} /> Edit
                      </button>
                      <button data-testid={`draft-discard-btn-${m.id}`} onClick={() => discardDraft(m)}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-red-600 text-sm font-medium rounded-md px-3 py-1.5 transition-colors">
                        <Trash size={14} /> Discard
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {srcData && (
        <div data-testid="source-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setSrcData(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-900">{t("thread.raw_source", "Raw source")} — {srcData.subject}</span>
              <button data-testid="source-modal-close" onClick={() => setSrcData(null)} className="text-gray-400 hover:text-gray-800"><X size={18} /></button>
            </div>
            {!srcData.body_html && (
              <div className="px-4 pt-3 text-[11px] text-gray-500">{t("thread.no_html_source", "This email was synced before raw-source storage was enabled — showing the stored plain text.")}</div>
            )}
            <pre className="flex-1 overflow-auto scroll-thin p-4 text-xs text-gray-800 whitespace-pre-wrap break-all">{srcData.body_html || srcData.body}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
